import {
  RepaymentStrategy,
  RepaymentStrategy as Domain,
  RepaymentStrategyField,
} from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { RepaymentStrategy as API } from 'modules/cm/api/cases/repaymentStrategy/RepaymentStrategy';
import { mapPropertyToApi } from '../property/mapper/domainToApi';
import { mapEndowmentToApi } from '../endowment/mapper/domainToApi';
import { mapInvestmentToApi } from '../investment/mapper/domainToApi';

const apiBuilder = (domain: Partial<Domain>) => {
  const api: Partial<API> = {};

  return {
    add(fields: RepaymentStrategyField[]): void {
      const map = new Map([
        [
          RepaymentStrategyField.HaveSaleOfAnotherProperty,
          () => {
            api.hasAnotherPropertyToSale = domain[RepaymentStrategyField.HaveSaleOfAnotherProperty];
          },
        ],
        [
          RepaymentStrategyField.HaveEndownments,
          () => {
            api.hasEndowments = domain[RepaymentStrategyField.HaveEndownments];
          },
        ],
        [
          RepaymentStrategyField.HaveInvestments,
          () => {
            api.hasInvestments = domain[RepaymentStrategyField.HaveInvestments];
          },
        ],
        [
          RepaymentStrategyField.HaveOther,
          () => {
            api.hasOtherRepayments = domain[RepaymentStrategyField.HaveOther];
          },
        ],
        [
          RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing,
          () => {
            api.hasPropertyToSaleForDownsizing = domain[RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing];
          },
        ],
        [
          RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty,
          () => {
            api.anotherPropertyRepaymentValue = Number(
              domain[RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty],
            );
          },
        ],
        [
          RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing,
          () => {
            api.saleForDownsizingValue = Number(
              domain[RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing],
            );
          },
        ],
        [
          RepaymentStrategyField.RepaymentValueOfHaveEndownments,
          () => {
            api.endowmentsTotalRepayment = Number(domain[RepaymentStrategyField.RepaymentValueOfHaveEndownments]);
          },
        ],
        [
          RepaymentStrategyField.RepaymentValueOfHaveInvestments,
          () => {
            api.investmentsTotalRepayment = Number(domain[RepaymentStrategyField.RepaymentValueOfHaveInvestments]);
          },
        ],
        [
          RepaymentStrategyField.RepaymentValueOfHaveOther,
          () => {
            api.otherTotalRepayment = Number(domain[RepaymentStrategyField.RepaymentValueOfHaveOther]);
          },
        ],
        [
          RepaymentStrategyField.DetailsForOther,
          () => {
            api.otherRepaymentsDetails = domain[RepaymentStrategyField.DetailsForOther];
          },
        ],
      ]);

      fields.forEach((field) => {
        const adder = map.get(field);
        if (typeof adder === 'function') {
          adder();
        }
      });
    },
    getAPI() {
      return api;
    },
  };
};

export const mapPartialDomainToPartialApi = (domain: Partial<Domain>): Partial<API> => {
  const builder = apiBuilder(domain);
  builder.add(Object.keys(domain) as RepaymentStrategyField[]);

  return builder.getAPI();
};

const mapDomainToAPI = (domain: Domain): API => {
  const builder = apiBuilder(domain);
  builder.add(Object.keys(domain) as RepaymentStrategyField[]);

  return builder.getAPI() as API;
};

export const mapRepaymentStrategyToApi = (state: RepaymentStrategy): API => {
  return {
    hasPropertyToSaleForDownsizing: state[RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing],
    hasAnotherPropertyToSale: state[RepaymentStrategyField.HaveSaleOfAnotherProperty],
    hasInvestments: state[RepaymentStrategyField.HaveInvestments],
    hasEndowments: state[RepaymentStrategyField.HaveEndownments],
    hasOtherRepayments: state[RepaymentStrategyField.HaveOther],

    saleForDownsizingValue: state[RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]
      ? state[RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]
      : null,
    anotherPropertyRepaymentValue: state[RepaymentStrategyField.HaveSaleOfAnotherProperty]
      ? state[RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]
      : null,

    investmentsTotalRepayment: state[RepaymentStrategyField.HaveInvestments]
      ? state[RepaymentStrategyField.RepaymentValueOfHaveInvestments]
      : null,

    endowmentsTotalRepayment: state[RepaymentStrategyField.HaveEndownments]
      ? state[RepaymentStrategyField.RepaymentValueOfHaveEndownments]
      : null,

    otherTotalRepayment: state[RepaymentStrategyField.HaveOther]
      ? state[RepaymentStrategyField.RepaymentValueOfHaveOther]
      : null,
    otherRepaymentsDetails: state[RepaymentStrategyField.HaveOther]
      ? state[RepaymentStrategyField.DetailsForOther]
      : null,
    repaymentStrategyTotal: state[RepaymentStrategyField.RepaymentValueTotalCalculated],

    totalAmountToBeRepaid: state[RepaymentStrategyField.TotalAmountToBeRepaid],
    properties: state[RepaymentStrategyField.HaveSaleOfAnotherProperty] ? state.properties.map(mapPropertyToApi) : [],
    endowments: state[RepaymentStrategyField.HaveEndownments] ? state.endowments.map(mapEndowmentToApi) : [],
    investments: state[RepaymentStrategyField.HaveInvestments] ? state.investments.map(mapInvestmentToApi) : [],
  };
};

export default mapDomainToAPI;
